package com.patronat.top.core.network

import com.patronat.top.dto.ServerErrorResponse
import com.patronat.top.error.AppErrorCode
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.RedirectResponseException
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpStatusCode
import io.ktor.http.isSuccess
import io.ktor.utils.io.errors.IOException
import kotlinx.serialization.SerializationException

suspend inline fun <reified T> safeApiCall(
    crossinline apiCall: suspend () -> HttpResponse
): NetworkResult<T> {
    return try {
        val response = apiCall()

        if (response.status.isSuccess()) {
            val data = response.body<T>()
            NetworkResult.Success(data)
        } else {
            val errorInfo = parseServerError(response)
            NetworkResult.Error(
                type = errorInfo.first,
                message = errorInfo.second ?: "Error with status: ${response.status}",
                throwable = null
            )
        }
    } catch (e: ClientRequestException) {
        val errorInfo = parseServerError(e.response)
        NetworkResult.Error(
            type = errorInfo.first,
            message = errorInfo.second ?: "Client Error: ${e.message}",
            throwable = e
        )
    } catch (e: ServerResponseException) {
        val errorInfo = parseServerError(e.response)
        NetworkResult.Error(
            type = errorInfo.first,
            message = errorInfo.second ?: "Server Error: ${e.message}",
            throwable = e
        )
    } catch (e: RedirectResponseException) {
        NetworkResult.Error(
            type = AppErrorCode.UNKNOWN,
            message = "Redirect error: ${e.message}",
            throwable = e
        )
    } catch (e: SerializationException) {
        NetworkResult.Error(
            type = AppErrorCode.INTERNAL_ERROR,
            message = "Data parsing error: ${e.message}",
            throwable = e
        )
    } catch (e: IOException) {
        NetworkResult.Error(
            type = AppErrorCode.NETWORK_OFFLINE,
            message = "No internet connection",
            throwable = e
        )
    } catch (e: Exception) {
        NetworkResult.Error(
            type = AppErrorCode.UNKNOWN,
            message = e.message ?: "Unknown error",
            throwable = e
        )
    }
}

suspend fun parseServerError(response: HttpResponse): Pair<AppErrorCode, String?> {
    return try {
        val errorBody = response.body<ServerErrorResponse>()

        val errorCodeEnum = AppErrorCode.entries.find { it.name == errorBody.code || it.toString() == errorBody.code }
            ?: mapStatusToErrorCode(response.status)

        Pair(errorCodeEnum, errorBody.message)
    } catch (e: Exception) {
        val fallbackCode = mapStatusToErrorCode(response.status)
        Pair(fallbackCode, "Error: ${response.status.value}")
    }
}

fun mapStatusToErrorCode(status: HttpStatusCode): AppErrorCode {
    return when (status) {
        HttpStatusCode.Unauthorized -> AppErrorCode.UNAUTHORIZED
        HttpStatusCode.Forbidden -> AppErrorCode.FORBIDDEN
        HttpStatusCode.NotFound -> AppErrorCode.NOT_FOUND
        HttpStatusCode.Conflict -> AppErrorCode.CONFLICT
        HttpStatusCode.UnprocessableEntity -> AppErrorCode.UNPROCESSABLE
        HttpStatusCode.TooManyRequests -> AppErrorCode.TOO_MANY_REQUESTS

        HttpStatusCode.InternalServerError -> AppErrorCode.INTERNAL_ERROR
        HttpStatusCode.ServiceUnavailable -> AppErrorCode.SERVER_UNAVAILABLE
        HttpStatusCode.RequestTimeout, HttpStatusCode.GatewayTimeout -> AppErrorCode.TIMEOUT

        else -> AppErrorCode.UNKNOWN
    }
}